import { ShieldCheckIcon } from "@heroicons/react/24/outline";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorMessage, apiFieldErrors } from "../services/api";
import {
  confirmTwoFactor,
  disableTwoFactor,
  enableTwoFactor,
  fetchTwoFactorStatus,
  regenerateRecoveryCodes,
} from "../services/twoFactorService";
import PasswordPromptModal from "./PasswordPromptModal";
import RecoveryCodesDialog from "./RecoveryCodesDialog";
import { formatDate } from "../services/datetime";

/** Setup runs Enable -> Scan -> Confirm -> Save codes. */
const SETUP_STEPS = ["enable", "scan", "confirm", "save"];

/** How few codes may be left before the card nags about regenerating. */
const LOW_RECOVERY_CODES = 3;

export default function TwoFactorCard({ onToast }) {
  const { t, i18n } = useTranslation();

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  // The pending secret + QR, present only while setup is in progress.
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState("");
  const [confirming, setConfirming] = useState(false);

  // Which sensitive action the password prompt is standing in front of.
  const [prompt, setPrompt] = useState(null);
  const [promptBusy, setPromptBusy] = useState(false);
  const [promptError, setPromptError] = useState("");
  const [promptFieldError, setPromptFieldError] = useState("");

  const [recoveryCodes, setRecoveryCodes] = useState(null);

  const loadStatus = useCallback(async () => {
    try {
      setStatus(await fetchTwoFactorStatus());
      setLoadError("");
    } catch (err) {
      setLoadError(apiErrorMessage(err, t("twoFactor.loadFailed")));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const openPrompt = (kind) => {
    setPrompt(kind);
    setPromptError("");
    setPromptFieldError("");
  };

  const closePrompt = () => {
    if (!promptBusy) setPrompt(null);
  };

  /** One handler behind all three password-confirmed actions. */
  const handlePasswordConfirm = async (password) => {
    setPromptBusy(true);
    setPromptError("");
    setPromptFieldError("");

    try {
      if (prompt === "enable") {
        setSetup(await enableTwoFactor(password));
        setCode("");
        setCodeError("");
      } else if (prompt === "regenerate") {
        setRecoveryCodes(await regenerateRecoveryCodes(password));
        await loadStatus();
        onToast?.({ message: t("twoFactor.toast.regenerated") });
      } else if (prompt === "disable") {
        await disableTwoFactor(password);
        setSetup(null);
        await loadStatus();
        onToast?.({ message: t("twoFactor.toast.disabled") });
      }

      setPrompt(null);
    } catch (err) {
      // A wrong password comes back keyed to the field; anything else is a banner.
      const passwordError = apiFieldErrors(err).password?.[0];
      setPromptFieldError(passwordError ?? "");
      setPromptError(passwordError ? "" : apiErrorMessage(err, t("twoFactor.actionFailed")));
    } finally {
      setPromptBusy(false);
    }
  };

  const handleConfirmCode = async (e) => {
    e.preventDefault();
    setCodeError("");
    setConfirming(true);

    try {
      const data = await confirmTwoFactor(code);
      setRecoveryCodes(data.recovery_codes);
      setSetup(null);
      setCode("");
      await loadStatus();
      onToast?.({ message: t("twoFactor.toast.enabled") });
    } catch (err) {
      setCodeError(apiFieldErrors(err).code?.[0] ?? apiErrorMessage(err, t("twoFactor.actionFailed")));
    } finally {
      setConfirming(false);
    }
  };

  if (loading) {
    return <p className="py-16 text-center text-sm text-ink-2">{t("common.loading")}</p>;
  }

  const enabled = Boolean(status?.enabled);
  const remaining = status?.recovery_codes_remaining ?? 0;
  const confirmedAt = status?.confirmed_at ? formatDate(status.confirmed_at, i18n.language) : null;

  // Step 1 is reached as soon as a secret exists, step 2 once a full code is
  // typed, and step 3 only once 2FA is actually on.
  const currentStep = setup ? (code.length === 6 ? 2 : 1) : enabled ? 3 : 0;

  return (
    <div className="card p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span
            className={`grid h-10 w-10 flex-shrink-0 place-items-center rounded-lg ${
              enabled ? "bg-accent-wash text-accent-text" : "bg-raised text-ink-2"
            }`}
          >
            <ShieldCheckIcon className="h-5 w-5" />
          </span>
          <div>
            <h2 className="font-semibold">{t("twoFactor.title")}</h2>
            <p className="mt-1 text-sm text-ink-2">{t("twoFactor.subtitle")}</p>
          </div>
        </div>

        <span
          className={`flex-shrink-0 rounded-full px-3 py-1 text-xs font-medium ${
            enabled ? "bg-accent-wash text-accent-text" : "bg-raised text-ink-2"
          }`}
        >
          {enabled ? t("twoFactor.enabled") : t("twoFactor.disabled")}
        </span>
      </div>

      {loadError && (
        <div className="mt-4 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
          {loadError}
        </div>
      )}

      {/* Stepper — shown from the moment setup starts until it is finished. */}
      {(setup || enabled) && (
        <ol className="mt-5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
          {SETUP_STEPS.map((step, index) => (
            <li key={step} className="flex items-center gap-2">
              <span
                className={`flex items-center gap-2 rounded-full px-2 py-1 font-medium ${
                  index <= currentStep ? "bg-accent-wash text-accent-text" : "bg-raised text-ink-2"
                }`}
              >
                <span>{index + 1}</span>
                {t("twoFactor.steps." + step)}
              </span>
              {index < SETUP_STEPS.length - 1 && <span className="text-ink-muted">›</span>}
            </li>
          ))}
        </ol>
      )}

      {/* Setup in progress: scan the QR, then confirm a code from the app. */}
      {setup && (
        <form onSubmit={handleConfirmCode} className="mt-5 border-t border-line pt-5" noValidate>
          <p className="text-sm text-ink-2">{t("twoFactor.setup.scanHint")}</p>

          <div className="mt-4 flex flex-col items-start gap-5 sm:flex-row">
            {/* SVG built by our own API from the otpauth URI — no external call,
                and the secret never reaches a third-party image service. */}
            <div
              className="rounded-lg border border-line bg-surface p-2"
              aria-label={t("twoFactor.setup.qrAlt")}
              dangerouslySetInnerHTML={{ __html: setup.qr_code_svg }}
            />

            <div className="min-w-0 flex-1">
              <p className="field-label">{t("twoFactor.setup.manualEntry")}</p>
              <code
                dir="ltr"
                className="block select-all break-all rounded-lg bg-raised px-3 py-2 font-mono text-sm text-ink"
              >
                {setup.secret}
              </code>
              <p className="mt-1 text-xs text-ink-2">{t("twoFactor.setup.manualHint")}</p>

              <label className="field-label mt-4" htmlFor="setup-code">
                {t("twoFactor.setup.codeLabel")}
              </label>
              <input
                id="setup-code"
                type="text"
                inputMode="numeric"
                maxLength={6}
                dir="ltr"
                className="field-input text-center text-lg font-semibold tracking-[0.5em]"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                autoComplete="one-time-code"
                disabled={confirming}
              />
              {codeError && <p className="field-error">{codeError}</p>}

              <div className="mt-4 flex gap-2">
                <button type="submit" disabled={confirming || code.length !== 6} className="btn-primary">
                  {confirming ? t("twoFactor.working") : t("twoFactor.setup.confirm")}
                </button>
                <button
                  type="button"
                  onClick={() => setSetup(null)}
                  disabled={confirming}
                  className="btn-secondary"
                >
                  {t("common.cancel")}
                </button>
              </div>
            </div>
          </div>
        </form>
      )}

      {/* Steady state: an invitation to turn it on, or the management controls. */}
      {!setup && (
        <div className="mt-5 border-t border-line pt-5">
          {enabled ? (
            <>
              <p className="text-sm text-ink-2">
                {confirmedAt
                  ? t("twoFactor.enabledSince", { date: confirmedAt })
                  : t("twoFactor.enabledHint")}
              </p>
              <p className="mt-1 text-sm text-ink-2">
                {t("twoFactor.recoveryRemaining", { count: remaining })}
              </p>

              {remaining < LOW_RECOVERY_CODES && (
                <div className="mt-3 rounded-lg border border-warning-line bg-warning-wash px-3 py-2 text-sm text-warning-text">
                  {t("twoFactor.recoveryLow", { count: remaining })}
                </div>
              )}

              <div className="mt-4 flex flex-wrap gap-2">
                <button type="button" onClick={() => openPrompt("regenerate")} className="btn-secondary">
                  {t("twoFactor.regenerate")}
                </button>
                <button
                  type="button"
                  onClick={() => openPrompt("disable")}
                  className="rounded-lg border border-danger px-4 py-2 text-sm font-semibold text-danger-text transition hover:bg-danger-wash"
                >
                  {t("twoFactor.disable")}
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-ink-2">{t("twoFactor.disabledHint")}</p>
              <button type="button" onClick={() => openPrompt("enable")} className="btn-primary mt-4">
                {t("twoFactor.enable")}
              </button>
            </>
          )}
        </div>
      )}

      <PasswordPromptModal
        open={prompt !== null}
        danger={prompt === "disable"}
        busy={promptBusy}
        error={promptError}
        fieldError={promptFieldError}
        title={t("twoFactor.prompt." + (prompt ?? "enable") + ".title")}
        message={t("twoFactor.prompt." + (prompt ?? "enable") + ".message")}
        confirmLabel={t("twoFactor.prompt." + (prompt ?? "enable") + ".confirm")}
        onConfirm={handlePasswordConfirm}
        onClose={closePrompt}
      />

      <RecoveryCodesDialog
        open={recoveryCodes !== null}
        codes={recoveryCodes}
        onClose={() => setRecoveryCodes(null)}
      />
    </div>
  );
}
